import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { fetchAiQuizBank } from "../../../api";
import { TYPE_LABELS } from "../items";
import { GhostButton } from "../items/shared";
import QuestionEditor from "./QuestionEditor";

/**
 * QuizBankManager — staff view of every generated question for a topic
 * (docs/AI_TOPIC_QUIZ.md): filter by type / hidden / reported / edited,
 * open any question in QuestionEditor, hide or delete it. Opened from the
 * Quiz panel's "Manage questions" link.
 */
const FILTERS = [["all", "All"], ["reported", "Reported"], ["hidden", "Hidden"], ["edited", "Edited"]];

function summary(q) {
  const d = q.data || {};
  switch (q.type) {
    case "mc": return { ar: d.prompt_ar, en: d.choices?.[0], extra: `answer: ${d.choices?.[0] || ""}` };
    case "audio_mc": return { ar: d.audio_text, en: d.choices?.[0] };
    case "cloze_choice": return { ar: d.sentence_ar, en: d.sentence_en, extra: `answer: ${d.choices?.[0] || ""}` };
    case "match_pairs": return { en: (d.pairs || []).map((p) => `${p.ar} = ${p.en}`).join(" · ") };
    case "bucket_sort": return { en: (d.buckets || []).map((b) => b.label_en).join(" / ") + ` · ${(d.items || []).length} words` };
    case "true_false": return { en: `${d.statement_en} — ${d.is_true ? "true" : "false"}`, ar: d.evidence_ar };
    case "order_sentence": return { ar: d.sentence_ar, en: d.sentence_en };
    case "verb_form": return { ar: d.past, en: d.target_label_en };
    default: return {};
  }
}

export default function QuizBankManager({ topicId, onClose }) {
  const [bank, setBank] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [type, setType] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    fetchAiQuizBank(topicId).then(setBank).catch((e) => setError(e.message));
  }, [topicId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !editing && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, editing]);

  const questions = useMemo(() => (bank?.banks || []).flatMap((b) => b.questions.map((q) => ({ ...q, batch_no: b.batch_no }))), [bank]);
  const types = useMemo(() => [...new Set(questions.map((q) => q.type))], [questions]);
  const shown = questions.filter((q) =>
    (type === "all" || q.type === type) &&
    (filter === "all" || (filter === "reported" && q.reports?.length) || (filter === "hidden" && q.hidden) || (filter === "edited" && q.edited))
  );

  const replace = (saved) => setBank((b) => ({
    ...b,
    banks: b.banks.map((bk) => bk.id !== saved.bank_id ? bk : { ...bk, questions: bk.questions.map((q) => (q.id === saved.id ? { ...q, ...saved } : q)) }),
  }));
  const drop = (removed) => setBank((b) => ({
    ...b,
    banks: b.banks.map((bk) => bk.id !== removed.bank_id ? bk : { ...bk, questions: bk.questions.filter((q) => q.id !== removed.id) }),
  }));

  return ReactDOM.createPortal(
    <div className="dark-scope fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6" style={{ background: "rgba(0,0,0,0.6)" }}
         onClick={(e) => e.target === e.currentTarget && !editing && onClose()} role="dialog" aria-modal="true">
      <div className="surface-glass w-full max-w-5xl h-[92vh] rounded-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-4" style={{ borderBottom: "1px solid var(--panel-line)" }}>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide m-0" style={{ color: "var(--parchment-dim)" }}>Question bank</p>
            <p className="font-cormorant font-semibold text-xl m-0 truncate" style={{ color: "var(--parchment)" }}>{bank?.topic_name || "…"}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: "var(--parchment-dim)" }}>{shown.length} of {questions.length}</span>
            <GhostButton onClick={onClose}>Close</GhostButton>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-5 py-3" style={{ borderBottom: "1px solid var(--panel-line)" }}>
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)}
                    className="px-3 py-1 rounded-full text-xs"
                    style={filter === k ? { background: "var(--gold-bg)", color: "var(--gold-bright)", border: "1px solid var(--gold-dim)" } : { color: "var(--parchment-dim)", border: "1px solid var(--panel-line)" }}>
              {label}
            </button>
          ))}
          <select value={type} onChange={(e) => setType(e.target.value)} className="ml-auto rounded-lg px-2 py-1 text-xs"
                  style={{ background: "var(--ink)", color: "var(--parchment)", border: "1px solid var(--panel-line)" }}>
            <option value="all">All types</option>
            {types.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto scroll-elegant px-5 py-3 flex flex-col gap-2">
          {error && <p className="text-sm" style={{ color: "#e58c8c" }}>{error}</p>}
          {bank && questions.length === 0 && <p className="text-sm" style={{ color: "var(--parchment-dim)" }}>No question bank yet — start the quiz once (or prewarm) to generate it.</p>}
          {shown.map((q) => {
            const s = summary(q);
            return (
              <button key={`${q.bank_id}-${q.id}`} type="button" onClick={() => setEditing(q)}
                      className="text-left rounded-xl px-4 py-3 transition-[filter] hover:brightness-110"
                      style={{ background: "var(--ink)", border: `1px solid ${q.reports?.length ? "#dc5a5a" : "var(--panel-line)"}`, opacity: q.hidden ? 0.55 : 1 }}>
                <div className="flex items-center gap-2 flex-wrap text-[11px]" style={{ color: "var(--parchment-dim)" }}>
                  <span className="px-2 py-0.5 rounded-full" style={{ border: "1px solid var(--panel-line)", color: "var(--tag-violet)" }}>
                    {TYPE_LABELS[q.type] || q.type}{q.data?.variant ? ` · ${q.data.variant}` : ""}
                  </span>
                  <span>batch {q.batch_no}</span>
                  <span>difficulty {q.difficulty}</span>
                  {q.edited && <span style={{ color: "var(--gold-bright)" }}>edited</span>}
                  {q.hidden && <span>hidden</span>}
                  {q.reports?.length > 0 && <span style={{ color: "#e58c8c" }}>{q.reports.length} report(s)</span>}
                </div>
                <p className="text-sm m-0 mt-1.5" style={{ color: "var(--parchment)" }}>{q.prompt_en}</p>
                {s.ar && (
                  <p dir="rtl" lang="ar" className="font-arabic text-xl leading-[1.8] m-0 mt-1 text-right" style={{ color: "var(--parchment)" }}>{s.ar}</p>
                )}
                {(s.en || s.extra) && (
                  <p className="text-xs m-0 mt-1" style={{ color: "var(--parchment-dim)" }}>{[s.en, s.extra].filter(Boolean).join("  ·  ")}</p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {editing && (
        <QuestionEditor
          question={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => { replace(saved); setEditing(null); }}
          onDeleted={(removed) => { drop(removed); setEditing(null); }}
        />
      )}
    </div>,
    document.body
  );
}
